import { sql } from "drizzle-orm";
import {
  boolean,
  date,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";

/**
 * Phase 0 schema — Reseller → Account → Site three-level tenancy plus
 * memberships, custom domains, audit log, and feature flags.
 *
 * Conventions:
 * - Every site-scoped table has a `site_id` FK with `onDelete: "cascade"`.
 * - Every account-scoped table has an `account_id` FK with `onDelete: "cascade"`.
 * - Reseller-scoped tables similarly.
 * - All UUIDs default to `gen_random_uuid()` (Postgres pgcrypto / Neon native).
 * - Timestamps are `timestamptz`, defaulting to `now()`.
 * - JSONB blobs are typed via `$type<>()` so Drizzle returns the shape we want.
 *
 * Per ADR 0017 + docs/specs/audit-log.md, `audit_log` is append-only by
 * convention; the application role's UPDATE/DELETE permissions on this table
 * will be revoked at the database role level in Phase 4+.
 */

// ----------------------------------------------------------------------------
// Enums
// ----------------------------------------------------------------------------

export const accountMemberRoleEnum = pgEnum("account_member_role", [
  "owner",
  "admin",
  "editor",
  "viewer",
]);

export const siteMembershipRoleEnum = pgEnum("site_membership_role", [
  "manager",
  "editor",
  "viewer",
]);

export const resellerMemberRoleEnum = pgEnum("reseller_member_role", ["owner", "admin"]);

export const billingPlanEnum = pgEnum("billing_plan", [
  "free",
  "starter",
  "pro",
  "enterprise",
  "wholesale",
]);

export const domainStatusEnum = pgEnum("domain_status", [
  "pending_dns",
  "pending_ssl",
  "active",
  "error",
]);

export const domainKindEnum = pgEnum("domain_kind", ["platform_subdomain", "custom"]);

export const invitationKindEnum = pgEnum("invitation_kind", [
  "reseller_owner",
  "dealer_d2c",
  "dealer_reseller",
]);

// ----------------------------------------------------------------------------
// Reseller (optional white-label parent)
// ----------------------------------------------------------------------------

export const resellers = pgTable(
  "resellers",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    slug: text("slug").notNull(),
    brandName: text("brand_name").notNull(),
    brandLogo: text("brand_logo"),
    themeTokens: jsonb("theme_tokens").$type<Record<string, string>>(),
    // Curated admin-console accent preset key (see apps/web/lib/theme/admin-accents).
    // Null -> platform default ("signal-red"). Distinct from themeTokens.primary,
    // which is the reseller's free-form email/marketing brand color.
    adminAccent: text("admin_accent"),
    customAdminDomain: text("custom_admin_domain"),
    wholesalePriceTier: text("wholesale_price_tier"),
    stripeCustomerId: text("stripe_customer_id"),
    workosOrgId: text("workos_org_id"),
    settings: jsonb("settings").$type<{
      poweredByVisible?: boolean;
      [key: string]: unknown;
    }>(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    slugIdx: uniqueIndex("resellers_slug_idx").on(t.slug),
    customAdminDomainIdx: uniqueIndex("resellers_custom_admin_domain_idx").on(t.customAdminDomain),
    workosOrgIdx: uniqueIndex("resellers_workos_org_id_idx").on(t.workosOrgId),
  }),
);

export const resellerMembers = pgTable(
  "reseller_members",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    resellerId: uuid("reseller_id")
      .notNull()
      .references(() => resellers.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull(),
    role: resellerMemberRoleEnum("role").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    resellerUserUnique: uniqueIndex("reseller_members_reseller_user_idx").on(
      t.resellerId,
      t.userId,
    ),
    userIdx: index("reseller_members_user_id_idx").on(t.userId),
  }),
);

// ----------------------------------------------------------------------------
// Account (customer / billing entity)
// ----------------------------------------------------------------------------

export const accounts = pgTable(
  "accounts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    resellerId: uuid("reseller_id").references(() => resellers.id, {
      onDelete: "set null",
    }),
    billingPlan: billingPlanEnum("billing_plan").notNull().default("starter"),
    stripeCustomerId: text("stripe_customer_id"),
    workosOrgId: text("workos_org_id"),
    settings: jsonb("settings").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    workosOrgIdx: uniqueIndex("accounts_workos_org_id_idx").on(t.workosOrgId),
    resellerIdx: index("accounts_reseller_id_idx").on(t.resellerId),
  }),
);

export const accountMembers = pgTable(
  "account_members",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    accountId: uuid("account_id")
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull(),
    role: accountMemberRoleEnum("role").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    accountUserUnique: uniqueIndex("account_members_account_user_idx").on(t.accountId, t.userId),
    userIdx: index("account_members_user_id_idx").on(t.userId),
  }),
);

// ----------------------------------------------------------------------------
// Site (the tenant boundary)
// ----------------------------------------------------------------------------

export const sites = pgTable(
  "sites",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    accountId: uuid("account_id")
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    slug: text("slug").notNull(),
    name: text("name").notNull(),
    settings: jsonb("settings").$type<{
      theme?: {
        primary?: string;
        secondary?: string;
        accent?: string;
        [key: string]: string | undefined;
      };
      hours?: Record<string, string>;
      integrations?: {
        ga4?: string;
        metaPixel?: string;
        gtm?: string;
        tiktok?: string;
      };
      [key: string]: unknown;
    }>(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    slugIdx: uniqueIndex("sites_slug_idx").on(t.slug),
    accountIdx: index("sites_account_id_idx").on(t.accountId),
  }),
);

export const domains = pgTable(
  "domains",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    hostname: text("hostname").notNull(),
    siteId: uuid("site_id").references(() => sites.id, { onDelete: "cascade" }),
    resellerId: uuid("reseller_id").references(() => resellers.id, {
      onDelete: "cascade",
    }),
    status: domainStatusEnum("status").notNull().default("pending_dns"),
    isPrimary: boolean("is_primary").notNull().default(false),
    vercelDomainId: text("vercel_domain_id"),
    errorMessage: text("error_message"),
    kind: domainKindEnum("kind").notNull().default("custom"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    hostnameIdx: uniqueIndex("domains_hostname_idx").on(t.hostname),
    siteIdx: index("domains_site_id_idx").on(t.siteId),
    resellerIdx: index("domains_reseller_id_idx").on(t.resellerId),
  }),
);

export const siteMemberships = pgTable(
  "site_memberships",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    userId: text("user_id").notNull(),
    role: siteMembershipRoleEnum("role").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    siteUserUnique: uniqueIndex("site_memberships_site_user_idx").on(t.siteId, t.userId),
    userIdx: index("site_memberships_user_id_idx").on(t.userId),
  }),
);

// ----------------------------------------------------------------------------
// Feature flags (Phase 0; Postgres-backed gradual-rollout primitive per ADR 8)
// ----------------------------------------------------------------------------

export const featureFlags = pgTable(
  "feature_flags",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    key: text("key").notNull(),
    description: text("description"),
    enabledGlobally: boolean("enabled_globally").notNull().default(false),
    enabledForAccountIds: jsonb("enabled_for_account_ids")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    enabledForSiteIds: jsonb("enabled_for_site_ids")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    enabledForResellerIds: jsonb("enabled_for_reseller_ids")
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    rolloutPercent: integer("rollout_percent").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    keyIdx: uniqueIndex("feature_flags_key_idx").on(t.key),
  }),
);

// ----------------------------------------------------------------------------
// Legal documents (privacy / terms — platform canonical, reseller, or site)
// ----------------------------------------------------------------------------

/**
 * Legal documents (privacy / terms). One row per (scope, scopeId, kind):
 *   scope 'platform' (scopeId null) = the canonical template (placeholders),
 *   scope 'reseller' (scopeId=resellerId) = a roster-wide override,
 *   scope 'site' (scopeId=siteId) = a per-site override.
 * status gates publication: the public never sees a 'draft'.
 */
export const legalDocuments = pgTable(
  "legal_documents",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    scope: text("scope").notNull(),
    scopeId: uuid("scope_id"),
    kind: text("kind").notNull(),
    bodyMarkdown: text("body_markdown").notNull(),
    version: integer("version").notNull().default(1),
    effectiveDate: date("effective_date"),
    status: text("status").notNull().default("draft"),
    updatedBy: text("updated_by"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [uniqueIndex("legal_documents_scope_kind_idx").on(t.scope, t.scopeId, t.kind)],
);

export type LegalDocument = typeof legalDocuments.$inferSelect;
export type NewLegalDocument = typeof legalDocuments.$inferInsert;

// ----------------------------------------------------------------------------
// Invitations (one primitive for reseller-owner, dealer-d2c, dealer-reseller)
// ----------------------------------------------------------------------------

export const invitations = pgTable(
  "invitations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    kind: invitationKindEnum("kind").notNull(),
    email: text("email").notNull(),
    tokenHash: text("token_hash").notNull(),
    resellerId: uuid("reseller_id").references(() => resellers.id, {
      onDelete: "cascade",
    }),
    accountName: text("account_name"),
    invitedBy: text("invited_by").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    claimedAt: timestamp("claimed_at", { withTimezone: true }),
    claimedBy: text("claimed_by"),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    siteId: uuid("site_id").references(() => sites.id, { onDelete: "cascade" }),
    role: accountMemberRoleEnum("role"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    tokenHashIdx: uniqueIndex("invitations_token_hash_idx").on(t.tokenHash),
    emailActiveIdx: index("invitations_email_active_idx").on(
      t.email,
      t.claimedAt,
      t.revokedAt,
      t.expiresAt,
    ),
    resellerActiveIdx: index("invitations_reseller_active_idx").on(t.resellerId, t.claimedAt),
    siteIdIdx: index("invitations_site_id_idx").on(t.siteId),
  }),
);

// ----------------------------------------------------------------------------
// Audit log (ADR 0017)
// ----------------------------------------------------------------------------

export const auditLog = pgTable(
  "audit_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    at: timestamp("at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    actorUserId: text("actor_user_id"),
    actorAccountId: uuid("actor_account_id"),
    actorResellerId: uuid("actor_reseller_id"),
    targetSiteId: uuid("target_site_id"),
    action: text("action").notNull(),
    resourceType: text("resource_type").notNull(),
    resourceId: text("resource_id").notNull(),
    payload: jsonb("payload").$type<Record<string, unknown>>(),
    requestId: text("request_id").notNull(),
  },
  (t) => ({
    targetSiteAtIdx: index("audit_log_target_site_at_idx").on(t.targetSiteId, t.at),
    actorAccountAtIdx: index("audit_log_actor_account_at_idx").on(t.actorAccountId, t.at),
    actionAtIdx: index("audit_log_action_at_idx").on(t.action, t.at),
    requestIdx: index("audit_log_request_id_idx").on(t.requestId),
  }),
);

// ----------------------------------------------------------------------------
// Inferred types for callers
// ----------------------------------------------------------------------------

export type Reseller = typeof resellers.$inferSelect;
export type NewReseller = typeof resellers.$inferInsert;
export type ResellerMember = typeof resellerMembers.$inferSelect;
export type NewResellerMember = typeof resellerMembers.$inferInsert;
export type Account = typeof accounts.$inferSelect;
export type NewAccount = typeof accounts.$inferInsert;
export type AccountMember = typeof accountMembers.$inferSelect;
export type NewAccountMember = typeof accountMembers.$inferInsert;
export type Site = typeof sites.$inferSelect;
export type NewSite = typeof sites.$inferInsert;
export type Domain = typeof domains.$inferSelect;
export type NewDomain = typeof domains.$inferInsert;
export type SiteMembership = typeof siteMemberships.$inferSelect;
export type NewSiteMembership = typeof siteMemberships.$inferInsert;
export type FeatureFlag = typeof featureFlags.$inferSelect;
export type NewFeatureFlag = typeof featureFlags.$inferInsert;
export type AuditLogRow = typeof auditLog.$inferSelect;
export type NewAuditLogRow = typeof auditLog.$inferInsert;
export type Invitation = typeof invitations.$inferSelect;
export type NewInvitation = typeof invitations.$inferInsert;
export type InvitationKind = (typeof invitationKindEnum.enumValues)[number];

// ----------------------------------------------------------------------------
// Tenant-scoping registry — used by withSite()/withAccount()/withReseller()
// ----------------------------------------------------------------------------

/**
 * Source of truth for which scope a given table belongs to. Used at runtime
 * by `lib/drizzle/with-site.ts` to refuse queries that touch tenant-scoped
 * tables without the appropriate filter.
 */
export const TENANT_SCOPE = {
  // Site-scoped (every read/write must filter by site_id)
  sites: "site",
  domains: "site-or-reseller", // Domain table can hold either
  site_memberships: "site",
  // Account-scoped
  accounts: "account",
  account_members: "account",
  // Reseller-scoped
  resellers: "reseller",
  reseller_members: "reseller",
  // Invitations: dealer_reseller / reseller_owner kinds carry reseller_id;
  // dealer_d2c has reseller_id = NULL and is scoped only by invitedBy.
  // Mirrors the `domains` precedent.
  invitations: "site-or-reseller",
  // Vehicles and leads (Phase 2)
  vehicles: "site",
  lead_capture_forms: "site",
  leads: "site",
  // Feed pipeline (Phase 2)
  feed_configs: "site",
  feed_runs: "site",
  export_configs: "site",
  export_runs: "site",
  dealer_addons: "site",
  // Legal documents: platform canonical has no scope; reseller/site overrides mirror domains.
  legal_documents: "site-or-reseller",
  // Cross-tenant
  feature_flags: "global",
  audit_log: "audit",
  // Platform-level marketing (not site-scoped — inbound prospects from public landing page)
  marketing_leads: "global",
} as const;

export type TenantScope = (typeof TENANT_SCOPE)[keyof typeof TENANT_SCOPE];
